<template>
  <button class="btn" :disabled="processing ? true : undefined" @click.prevent="handleShelf">
    <i v-if="shelfStatus" class="material-symbols-outlined fill" aria-hidden="true">bookmark</i>
    <i v-else class="material-symbols-outlined" aria-hidden="true">bookmark_add</i>
    {{ shelfText }}
  </button>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import type { PropType } from 'vue'
import { mapState, mapActions } from 'pinia'
import { useShelvesStore } from '@/stores/shelves'
import { fbApi, FbApiError } from '@/utils/http-api'

/**
 * Add / remove items from user shelf.
 */
export default defineComponent({
  name: 'ShelfButton',
  props: {
    entityId: {
      type: Number,
      required: true,
    },
    entityType: {
      type: String as PropType<'plan'>,
      default: 'plan',
      validator: (val: string) => ['plan'].includes(val),
    },
  },
  data() {
    return {
      processing: false,
    }
  },

  computed: {
    ...mapState(useShelvesStore, ['isOnShelf']),

    // used to instantly change shelf status while XHR processing
    shelfStatus() {
      const isOnShelf = this.isOnShelf({
        shelvableId: this.entityId,
        shelvableType: this.entityType,
      })

      // if loading, flip value quickly so user gets instant response
      if (this.processing) {
        return !isOnShelf
      }

      // default
      return isOnShelf
    },

    shelfText() {
      return this.shelfStatus ? 'Remove from Shelf' : 'Add to Your Shelf'
    },
  },
  methods: {
    ...mapActions(useShelvesStore, ['setShelfCards', 'addShelfItem', 'removeShelfItem']),

    // xhr for add or delete shelvable
    async handleShelf() {
      // do not double submit
      if (this.processing) {
        return
      }

      this.processing = true
      let deleteRequest = false
      const payload = {
        shelvableId: this.entityId,
        shelvableType: this.entityType,
      }

      const isOnShelf = this.isOnShelf(payload)

      try {
        let res = null

        if (!isOnShelf) {
          // add to shelf
          res = await fbApi.post('/shelves', {
            json: payload,
          })

          // missing data
          if (!res || !res.body || !res.body.data) {
            throw Error('Shelf fetch missing response')
          }

          // update all cards
          this.setShelfCards(res.body.data.items)

          // update shelf icons in plan menu
          this.addShelfItem(payload)
        } else {
          // remove from shelf
          deleteRequest = true

          await fbApi.delete(`/shelves/${this.entityType}/${this.entityId}`)

          // remove of shelf w/o resetting list
          this.removeShelfItem(payload)
        }
      } catch (e) {
        //  if delete and 404, still remove shelf item in ui
        if (deleteRequest && e instanceof FbApiError && e.status === 404) {
          this.removeShelfItem(payload)
        }
      } finally {
        this.processing = false
      }
    },
  },
})
</script>
